import { inject, Injectable } from '@angular/core';
import { map, Observable, of, shareReplay } from 'rxjs';

// EZ
import { CrudApiService } from '@template-ui/services/crud-api.service';
import { EzTextService } from '@template-ui/services/ez-text.service';
import { InputSelectOption } from '@template-ui/types/Input-select-option';
import { DropdownOptionBuilder } from '@template-ui/helpers/dropdown-builder.helper';
import { EZDropdown, IDropdownOptionsService } from "@template-ui/types";
import { read } from '@template-ui/helpers/signal.helpers';

// BITS
import { commonDropdownConfigFactories, DropdownKey } from '../dropdown-configs/common.dropdown-config';

/**
 * Service for managing dropdown options with support for static data, API fetching, and caching.
 *
 * Features:
 * - Retrieves options from static lists or HTTP endpoints
 * - Supports parent-dependent filtering for cascading dropdowns
 * - In-memory caching to avoid redundant API calls
 * - Template-based label resolution
 * - Configurable HTTP methods (GET/POST)
 */
@Injectable({ providedIn: 'root' })
export class DropdownOptionsService implements IDropdownOptionsService {

  // ───────────────────────────────────────────────────────────────
  //  Dependency Injection
  // ───────────────────────────────────────────────────────────────
  private readonly crudService = inject(CrudApiService);
  private readonly textTemplateService = inject(EzTextService);

  // ───────────────────────────────────────────────────────────────
  //  Internal State
  // ───────────────────────────────────────────────────────────────
  /** In-memory cache of transformed dropdown options, keyed by computed identifier. */
  private memoryCache = new Map<string, InputSelectOption[]>();

  /** Prefix for cache keys to avoid collisions. */
  private readonly storagePrefix = 'dropdown-options-';

  /** Sentinel value used for "All" option in dropdowns. */
  private readonly ALL = '__ALL__';

  // ───────────────────────────────────────────────────────────────
  //  Public Methods
  // ───────────────────────────────────────────────────────────────
  /**
   * Retrieves a registered dropdown configuration by key.
   *
   * @param key - The registered dropdown identifier (e.g., 'participants', 'agencies')
   * @returns The dropdown configuration object for the given key
   */
  public getConfig<T>(key: string): EZDropdown<T> {
    const factory = commonDropdownConfigFactories[key as DropdownKey];
    return factory() as EZDropdown;
  }

  /**
   * Retrieves dropdown options using a config. Supports static or API-driven options.
   *
   * Applies caching (if enabled), parent-dependent filtering, label templating, and transformations.
   *
   * @param config - Dropdown configuration object with endpoint, static items, or key
   * @param parentValue - Optional parent value used to filter dropdown items in cascading scenarios
   * @param parentValues - Optional map of all parent dropdown values for multi-parent filtering
   * @returns Observable stream of transformed dropdown options
   *
   * @example
   * ```ts
   * this.dropdownService.get(investigatorConfig).subscribe(options => {
   *   this.investigators = options;
   * });
   * ```
   */
    get<T>(config: EZDropdown<T>, parentValue?: any, parentValues?: Map<EZDropdown<any>, any>): Observable<InputSelectOption[]> {
        const method = config.endpointVerbType ?? 'GET';
        const cacheKey = this.generateCacheKey(config);
        const storageKey = this.storagePrefix + cacheKey;
        const allValue = this.getAllOptionValue(config);

        const applyFilters = (items: InputSelectOption[]) => {
            if (config.transformOnParents && parentValues) {
                return config.transformOnParents(parentValues, items as any);
            }
            if (parentValues && config.filterOnParents) {
                return config.filterOnParents(parentValues, items as any);
            }
            if (config.filterOnParentValue && parentValue !== undefined && parentValue !== allValue) {
                return config.filterOnParentValue(parentValue, items as any);
            }
            return items;
        };

        if (config.cacheDropdown) {
            if (this.memoryCache.get(storageKey)) {
                const cached = this.memoryCache.get(storageKey) as InputSelectOption[];
                const filtered = applyFilters(cached);
                return of(filtered as InputSelectOption[]);
            }
        }

        if (config.staticItems) {
            return this.getStaticOptions(config, storageKey, parentValue, parentValues);
        }

        return this.getOptionsFromApi(config, storageKey, method, parentValue, parentValues);
    }

  // ───────────────────────────────────────────────────────────────
  //  Private Helper Methods
  // ───────────────────────────────────────────────────────────────
  /**
   * Retrieves the "All" option value from config, or returns the default sentinel.
   *
   * @param config - Dropdown configuration
   * @returns The configured all option value or the default '__ALL__' sentinel
   */
  private getAllOptionValue<T>(config: EZDropdown<T>): unknown {
    return (config as any).allOptionValue !== undefined ? (config as any).allOptionValue : this.ALL;
  }

  /**
   * Processes dropdown options from static items.
   *
   * Applies parent-dependent filtering, transformations, and caching.
   *
   * @param config - Dropdown configuration with static items
   * @param storageKey - Cache key for storing transformed options
   * @param parentValue - Optional parent value for single-parent filtering
   * @param parentValues - Optional map of parent values for multi-parent filtering
   * @returns Observable of transformed dropdown options
   */

    private getStaticOptions<T>(
        config: EZDropdown<T>,
        storageKey: string,
        parentValue: any,
        parentValues?: Map<EZDropdown<any>, any>
    ): Observable<InputSelectOption[]> {
        const allValue = (config as any).allOptionValue !== undefined ? (config as any).allOptionValue : this.ALL;
        // Handle both array and Signal<array> for staticItems
        let items = read(config.staticItems!);

        if (config.transformOnParents && parentValues) {
            items = config.transformOnParents(parentValues, items as any);
        }
        // Apply filterOnParents if present
        if (parentValues && config.filterOnParents) {
            items = config.filterOnParents(parentValues, items as any);
        }
        // Apply filterOnParentValue if present
        if (config.filterOnParentValue && parentValue && parentValue !== allValue) {
            items = config.filterOnParentValue(parentValue, items);
        }


        const opts = this.buildOptions(items, config);
        if (config.cacheDropdown) {
            // Store in memory cache. localStorage implementation commented pending persistence requirements.
            this.memoryCache.set(storageKey, opts);
        }
        return of(opts);
    }

  /**
   * Fetches dropdown data via HTTP request and transforms it into dropdown options.
   *
   * Applies filtering, transformations, and caches the result if enabled.
   *
   * @param config - Configuration describing endpoint, method, and filters
   * @param storageKey - Cache key for storing transformed options
   * @param method - HTTP method to use ('GET' or 'POST')
   * @param parentValue - Optional parent value for single-parent filtering
   * @param parentValues - Optional map of parent values for multi-parent filtering
   * @returns Observable of transformed dropdown options with shareReplay for efficient subscription handling
   */
    private getOptionsFromApi<T>(
        config: EZDropdown<T>,
        storageKey: string,
        method: string,
        parentValue: any,
        parentValues?: Map<EZDropdown<any>, any>
    ): Observable<InputSelectOption[]> {
        const request$ = method === 'GET'
            ? this.crudService.GetList<InputSelectOption>(config.endpoint!)
            : this.crudService.PostToGetList<InputSelectOption>(config.endpoint!, config.body ?? {});

        const allValue = this.getAllOptionValue(config);
        return request$.pipe(
            map(response => {
                let items = this.crudService.getListFromResponse(response);
                if (config.filterAfterFetch) {
                    items = items.filter(config.filterAfterFetch);
                }
                if (config.transformOnParents && parentValues) {
                    items = config.transformOnParents(parentValues, items as any);
                }
                // Apply filterOnParents if present
                if (parentValues && config.filterOnParents) {
                    items = config.filterOnParents(parentValues, items as any);
                }
                if (config.filterOnParentValue && parentValue && parentValue !== allValue) {
                    items = config.filterOnParentValue(parentValue, items);
                }

                const opts = this.buildOptions(items, config);
                if (config.cacheDropdown) {
                    // Store in memory cache. localStorage implementation commented pending persistence requirements.
                    this.memoryCache.set(storageKey, opts);
                }
                return opts;
            }),
            shareReplay(1)
        );
    }

  /**
   * Converts a list of items into dropdown options using provided config mappings and templates.
   *
   * Handles label resolution (via template or getLabel function), adds "All" and "Clear"
   * options if configured, and maintains option order.
   *
   * @param items - List of items to convert
   * @param config - Configuration for extracting value and label from items
   * @returns List of transformed options ready for dropdown rendering
   *
   * @example
   * ```ts
   * const items = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }];
   * const config: EZDropdown = {
   *   name: 'users',
   *   getValue: (item) => item.id,
   *   getLabel: (item) => item.name,
   *   includeAllOption: true
   * };
   * const options = this.buildOptions(items, config);
   * // Result: [{ value: '__ALL__', viewValue: 'All' },
   * //          { value: 1, viewValue: 'Alice' },
   * //          { value: 2, viewValue: 'Bob' }]
   * ```
   */
    private buildOptions<T>(items: T[], config: EZDropdown<T>): InputSelectOption[] {
        const getLabel = config.labelTemplate
            ? (item: T) => this.textTemplateService.resolveTemplate(config.labelTemplate!, item)
            : config.getLabel!;
        const options = DropdownOptionBuilder.createOptions(items, config.getValue, getLabel);
        const allValue = (config as any).allOptionValue !== undefined ? (config as any).allOptionValue : this.ALL;
        if (config.includeAllOption) {
            options.unshift({ value: allValue, viewValue: config.allOptionLabel ?? 'All' });
        }
        if (config.includeClearOption) {
            options.unshift({ value: null, viewValue: config.clearOptionLabel ?? 'Clear Selection' });
        }

        return options;
    }

  /**
   * Creates a unique cache key for dropdown options based on config details.
   *
   * Includes name, HTTP method, endpoint, and request body to ensure unique keys
   * for different dropdown configurations.
   *
   * @param config - Dropdown configuration
   * @returns Unique cache key string (format: `{name}:{method}:{endpoint}:{bodyHash}`)
   *
   * @example
   * ```ts
   * const key = this.generateCacheKey(config);
   * // Result: 'investigators:POST:https://api.example.com/investigators:{}'
   * ```
   */
    private generateCacheKey<T>(config: EZDropdown<T>): string {
        const payloadKey = config.body ? JSON.stringify(config.body) : '';
        const method = config.endpointVerbType ?? 'GET';
        return `${config.name}:${method}:${config.endpoint}:${payloadKey}`;
    }

}
